/**
 * @module engine3d/solid/profile
 * @summary Arbitrary 2D profiles — the machinery behind hand-drawn sketches —
 *   and the fabricated shapes built from them.
 * @description
 *   `mesh.extrude` fans its end caps from the centroid, which it documents as
 *   adequate for the convex outlines the plant models use. A profile the USER
 *   drew is a different proposition: an L bracket, a U trough, a T rib and a C
 *   channel are all non-convex, and a centroid fan on any of them throws
 *   triangles straight across the opening. So anything that starts from a
 *   drawn outline comes through here instead, where the caps are properly
 *   triangulated and the winding is fixed no matter which way round it was
 *   drawn.
 *
 *   Same conventions as `mesh.ts`: profiles are `Vec2` with `.x` / `.y` reading
 *   as the two axes of the sketch plane, solids sit on y = 0, and every builder
 *   returns per-vertex normals.
 *
 *   // INVARIANT: pure geometry — no engine state, no DOM.
 */
import { emptyMesh, type Mesh } from '../mesh';
import { TAU, v3, vCross, vNorm, vSub, type Vec2, type Vec3 } from '../math3';

/** Append a vertex, returning its index. */
function push(m: Mesh, p: Vec3, n: Vec3): number {
  const i = m.pos.length / 3;
  m.pos.push(p.x, p.y, p.z);
  m.nrm.push(n.x, n.y, n.z);
  return i;
}

/** Append a quad as two triangles, skipping either if it is degenerate. */
function quad(m: Mesh, a: Vec3, b: Vec3, c: Vec3, d: Vec3, n: Vec3): void {
  const same = (p: Vec3, q: Vec3) =>
    Math.abs(p.x - q.x) < 1e-5 && Math.abs(p.y - q.y) < 1e-5 && Math.abs(p.z - q.z) < 1e-5;
  const ia = push(m, a, n);
  const ib = push(m, b, n);
  const ic = push(m, c, n);
  const id = push(m, d, n);
  if (!same(a, b) && !same(b, c)) m.idx.push(ia, ib, ic);
  if (!same(a, c) && !same(c, d)) m.idx.push(ia, ic, id);
}

// ─── Polygon hygiene ─────────────────────────────────────────────────────

/** Signed area. Positive means the winding the wall builder expects. */
export function signedArea(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0, n = poly.length; i < n; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % n];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

/**
 * Force the winding the wall builder needs.
 * Without this, a profile drawn clockwise comes out with every normal pointing
 * inward — and since the rasteriser culls on the authored normal, the solid
 * simply is not there.
 */
export function ensureCCW(poly: Vec2[]): Vec2[] {
  return signedArea(poly) < 0 ? poly.slice().reverse() : poly.slice();
}

/** Drop duplicate vertices, and a closing vertex that repeats the first. */
export function cleanPoly(poly: Vec2[], eps = 0.05): Vec2[] {
  const out: Vec2[] = [];
  for (const p of poly) {
    const last = out[out.length - 1];
    if (last && Math.hypot(p.x - last.x, p.y - last.y) < eps) continue;
    out.push({ x: p.x, y: p.y });
  }
  while (out.length > 1) {
    const a = out[0];
    const b = out[out.length - 1];
    if (Math.hypot(a.x - b.x, a.y - b.y) < eps) out.pop();
    else break;
  }
  return out;
}

/**
 * Ear-clipping triangulation of a simple polygon, as index triples into `poly`.
 * Expects counter-clockwise winding — run `ensureCCW` first.
 *
 * O(n²), which on a hand-drawn profile of a few dozen points is microseconds,
 * and it runs only when the profile CHANGES, never per frame. If the polygon
 * defeats it — self-intersecting, say — it emits what it has and fans the
 * remainder, so a pathological sketch still yields a solid rather than nothing.
 */
export function triangulate(poly: Vec2[]): number[] {
  const n = poly.length;
  if (n < 3) return [];
  const idx: number[] = [];
  const live: number[] = [];
  for (let i = 0; i < n; i++) live.push(i);

  const cross = (o: Vec2, a: Vec2, b: Vec2) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  /** Strictly inside triangle abc? */
  const inside = (a: Vec2, b: Vec2, c: Vec2, p: Vec2): boolean => {
    const d1 = cross(a, b, p);
    const d2 = cross(b, c, p);
    const d3 = cross(c, a, p);
    return (d1 > 0 && d2 > 0 && d3 > 0) || (d1 < 0 && d2 < 0 && d3 < 0);
  };

  let guard = n * n + 16;
  while (live.length > 3 && guard-- > 0) {
    let clipped = false;
    for (let k = 0; k < live.length; k++) {
      const i0 = live[(k + live.length - 1) % live.length];
      const i1 = live[k];
      const i2 = live[(k + 1) % live.length];
      const a = poly[i0];
      const b = poly[i1];
      const c = poly[i2];
      // A convex corner — a left turn, given CCW winding.
      if (cross(a, b, c) <= 0) continue;
      // …and an empty one: no other vertex inside the candidate ear.
      let empty = true;
      for (const j of live) {
        if (j === i0 || j === i1 || j === i2) continue;
        if (inside(a, b, c, poly[j])) {
          empty = false;
          break;
        }
      }
      if (!empty) continue;
      idx.push(i0, i1, i2);
      live.splice(k, 1);
      clipped = true;
      break;
    }
    if (!clipped) break; // Not a simple polygon; stop rather than spin.
  }
  if (live.length === 3) idx.push(live[0], live[1], live[2]);
  else if (live.length > 3)
    for (let k = 1; k < live.length - 1; k++) idx.push(live[0], live[k], live[k + 1]);
  return idx;
}

// ─── Extrusion ───────────────────────────────────────────────────────────

/**
 * Extrude a closed profile by `h` along +Y with properly triangulated caps.
 * Same contract as `mesh.extrude` — profile in the XZ plane, sitting on y = 0 —
 * but safe for any simple outline, convex or not.
 */
export function extrudePoly(poly2: Vec2[], h: number): Mesh {
  const poly = ensureCCW(cleanPoly(poly2));
  const n = poly.length;
  const m = emptyMesh();
  if (n < 3) return m;

  // Walls: one hard-edged quad per edge.
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const nr = vNorm(v3(b.y - a.y, 0, -(b.x - a.x)));
    quad(m, v3(a.x, 0, a.y), v3(b.x, 0, b.y), v3(b.x, h, b.y), v3(a.x, h, a.y), nr);
  }

  const tri = triangulate(poly);
  const cap = (y: number, up: boolean) => {
    const nr = v3(0, up ? 1 : -1, 0);
    const base = m.pos.length / 3;
    for (const p of poly) push(m, v3(p.x, y, p.y), nr);
    for (let k = 0; k < tri.length; k += 3) {
      const a = base + tri[k];
      const b = base + tri[k + 1];
      const c = base + tri[k + 2];
      if (up) m.idx.push(a, b, c);
      else m.idx.push(a, c, b);
    }
  };
  cap(h, true);
  cap(0, false);
  return m;
}

/** Which world axis a profile is extruded along. */
export type ExtrudeAxis = 'x' | 'y' | 'z';

/**
 * Remap a mesh built along +Y onto another axis.
 *
 * Both remappings are PROPER rotations, deliberately: a coordinate swap that
 * happens to be a reflection turns every normal inside out, and the solid then
 * vanishes when you orbit behind it. Writing (u, t, v) for profile-horizontal,
 * extrusion and profile-vertical:
 *
 *   y : (u, t, v) → (u,  t,  v)   profile in XZ, grows up          (identity)
 *   z : (u, t, v) → (u,  v, -t)   profile in XY, grows toward +Z
 *   x : (u, t, v) → (t,  v,  u)   profile in ZY, grows toward +X
 */
export function remapAxis(m: Mesh, axis: ExtrudeAxis, len: number): Mesh {
  if (axis === 'y') return m;
  const swap = (arr: number[], shift: number) => {
    for (let i = 0; i < arr.length; i += 3) {
      const u = arr[i];
      const t = arr[i + 1];
      const v = arr[i + 2];
      if (axis === 'z') {
        arr[i] = u;
        arr[i + 1] = v;
        arr[i + 2] = -t + shift;
      } else {
        arr[i] = t + shift;
        arr[i + 1] = v;
        arr[i + 2] = u;
      }
    }
  };
  swap(m.pos, axis === 'z' ? len : 0);
  // Normals take the same rotation, without the translation.
  swap(m.nrm, 0);
  return m;
}

/** Extrude a closed profile by `len` along any axis. */
export function extrudeAlong(poly: Vec2[], len: number, axis: ExtrudeAxis): Mesh {
  return remapAxis(extrudePoly(poly, len), len === 0 ? 'y' : axis, len);
}

/**
 * Map a sketch-plane point into world space.
 * The inverse of what `remapAxis` does to a mesh, so a sweep path and an
 * extruded profile drawn on the same plane end up in the same place.
 */
export function planePoint(p: Vec2, axis: ExtrudeAxis, t = 0): Vec3 {
  if (axis === 'y') return v3(p.x, t, p.y);
  if (axis === 'z') return v3(p.x, p.y, -t);
  return v3(t, p.y, p.x);
}

// ─── Fabricated shapes ───────────────────────────────────────────────────

/**
 * A square-to-round transition: a `w × d` rectangle at y = 0 lofting to a
 * circle of radius `r` at y = h, or the other way about with `flip`.
 *
 * Neither a cone nor a rectangular loft can stand in for this, and it is the
 * fitting between a hopper's square outlet and a round spout — which is to say
 * it appears on nearly every bulk-solids drawing there is.
 */
export function squareToRound(
  w: number,
  d: number,
  r: number,
  h: number,
  flip = false,
  seg = 32,
): Mesh {
  const m = emptyMesh();
  const n = Math.max(8, Math.round(seg));
  const a = w / 2;
  const b = d / 2;

  /** Where a ray at angle `t` from the centre leaves the rectangle. */
  const onRect = (t: number): Vec2 => {
    const cs = Math.cos(t);
    const sn = Math.sin(t);
    const tx = Math.abs(cs) > 1e-9 ? a / Math.abs(cs) : Infinity;
    const tz = Math.abs(sn) > 1e-9 ? b / Math.abs(sn) : Infinity;
    const k = Math.min(tx, tz);
    return { x: cs * k, y: sn * k };
  };

  const lo: Vec3[] = [];
  const hi: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const t = (TAU * i) / n;
    const rect = onRect(t);
    const circ = { x: Math.cos(t) * r, y: Math.sin(t) * r };
    const bottom = flip ? circ : rect;
    const top = flip ? rect : circ;
    lo.push(v3(bottom.x, 0, bottom.y));
    hi.push(v3(top.x, h, top.y));
  }

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const e1 = vSub(lo[j], lo[i]);
    const e2 = vSub(hi[i], lo[i]);
    const nr = vNorm(vCross(e1, e2));
    quad(m, lo[i], lo[j], hi[j], hi[i], v3(-nr.x, -nr.y, -nr.z));
  }

  // Caps: a fan is safe, both ends being convex.
  const capFan = (ring: Vec3[], y: number, up: boolean) => {
    const nr = v3(0, up ? 1 : -1, 0);
    const ctr = push(m, v3(0, y, 0), nr);
    const ids = ring.map((p) => push(m, p, nr));
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      if (up) m.idx.push(ctr, ids[i], ids[j]);
      else m.idx.push(ctr, ids[j], ids[i]);
    }
  };
  capFan(lo, 0, false);
  capFan(hi, h, true);
  return m;
}

/**
 * An open cylindrical shell with real wall thickness: outer face, inner face and
 * two rims, open at both ends so you can see down inside it.
 *
 * With no boolean cuts available this is how a hollow thing gets drawn — and it
 * is the honest way round, because an open bin or a duct genuinely IS a rolled
 * plate with two visible faces and an edge you could put a rule on.
 */
export function shellTube(rOuter: number, wall: number, h: number, seg = 32): Mesh {
  const m = emptyMesh();
  const n = Math.max(6, Math.round(seg));
  const ri = Math.max(0.5, rOuter - Math.max(0.5, wall));
  for (let i = 0; i < n; i++) {
    const t0 = (TAU * i) / n;
    const t1 = (TAU * (i + 1)) / n;
    const c0 = Math.cos(t0);
    const s0 = Math.sin(t0);
    const c1 = Math.cos(t1);
    const s1 = Math.sin(t1);
    // Outer face, normals out.
    quad(
      m,
      v3(c0 * rOuter, 0, s0 * rOuter),
      v3(c1 * rOuter, 0, s1 * rOuter),
      v3(c1 * rOuter, h, s1 * rOuter),
      v3(c0 * rOuter, h, s0 * rOuter),
      vNorm(v3(c0 + c1, 0, s0 + s1)),
    );
    // Inner face, normals in.
    quad(
      m,
      v3(c1 * ri, 0, s1 * ri),
      v3(c0 * ri, 0, s0 * ri),
      v3(c0 * ri, h, s0 * ri),
      v3(c1 * ri, h, s1 * ri),
      vNorm(v3(-(c0 + c1), 0, -(s0 + s1))),
    );
    // Rims, top and bottom.
    quad(
      m,
      v3(c0 * ri, h, s0 * ri),
      v3(c0 * rOuter, h, s0 * rOuter),
      v3(c1 * rOuter, h, s1 * rOuter),
      v3(c1 * ri, h, s1 * ri),
      v3(0, 1, 0),
    );
    quad(
      m,
      v3(c1 * ri, 0, s1 * ri),
      v3(c1 * rOuter, 0, s1 * rOuter),
      v3(c0 * rOuter, 0, s0 * rOuter),
      v3(c0 * ri, 0, s0 * ri),
      v3(0, -1, 0),
    );
  }
  return m;
}

/**
 * A U-section trough running along +X, open at the top: a half-round bottom
 * with straight sides, in plate of thickness `wall`.
 *
 * The one builder here that does NOT grow along +Y, because a trough is
 * inherently horizontal — a screw conveyor casing, a feeder pan. Building it
 * upright and leaving the user to rotate it would put the opening somewhere they
 * have to work out rather than see.
 *
 * The cross-section is a single closed loop, so it extrudes with proper end
 * plates — which is what the real item has, and why it needs the triangulator.
 */
export function troughU(rOuter: number, sideH: number, wall: number, len: number): Mesh {
  const w = Math.max(1, Math.min(wall, rOuter - 1));
  const ri = rOuter - w;
  const seg = 20;
  const poly: Vec2[] = [];
  // Outer half-round: left, round the bottom, to the right.
  for (let i = 0; i <= seg; i++) {
    const t = Math.PI + (Math.PI * i) / seg;
    poly.push({ x: Math.cos(t) * rOuter, y: rOuter + Math.sin(t) * rOuter });
  }
  // Up the right wall and in across its top.
  poly.push({ x: rOuter, y: rOuter + sideH });
  poly.push({ x: ri, y: rOuter + sideH });
  // Inner half-round: right, round the bottom, back to the left.
  for (let i = seg; i >= 0; i--) {
    const t = Math.PI + (Math.PI * i) / seg;
    poly.push({ x: Math.cos(t) * ri, y: rOuter + Math.sin(t) * ri });
  }
  // Up the left wall and out across its top, closing on the start.
  poly.push({ x: -ri, y: rOuter + sideH });
  poly.push({ x: -rOuter, y: rOuter + sideH });
  return extrudeAlong(poly, len, 'x');
}

/** The structural section shapes on offer. */
export type SectionKind = 0 | 1 | 2 | 3;

/**
 * The cross-section of a rolled structural member, centred on the origin in the
 * XZ plane and ready to extrude along +Y.
 *
 * `kind`: 0 I-beam, 1 channel, 2 angle, 3 hollow section or flat bar. The
 * hollow section is a closed rectangle because that is exactly what an RHS looks
 * like from outside — there is no view in which its bore shows, so nothing is
 * lost by not having one.
 *
 * @param dep Overall depth, across Z.
 * @param wid Flange width, across X.
 * @param t   Plate thickness.
 */
export function sectionPoly(kind: SectionKind, dep: number, wid: number, t: number): Vec2[] {
  const hz = dep / 2;
  const hx = wid / 2;
  const w = Math.max(0.5, Math.min(t, Math.min(dep, wid) / 2.2));
  switch (kind) {
    case 0: {
      // I: two flanges joined by a web — twelve corners.
      const hw = w / 2;
      return [
        { x: -hx, y: -hz },
        { x: hx, y: -hz },
        { x: hx, y: -hz + w },
        { x: hw, y: -hz + w },
        { x: hw, y: hz - w },
        { x: hx, y: hz - w },
        { x: hx, y: hz },
        { x: -hx, y: hz },
        { x: -hx, y: hz - w },
        { x: -hw, y: hz - w },
        { x: -hw, y: -hz + w },
        { x: -hx, y: -hz + w },
      ];
    }
    case 1:
      // C: a web with both flanges turned the same way.
      return [
        { x: -hx, y: -hz },
        { x: hx, y: -hz },
        { x: hx, y: -hz + w },
        { x: -hx + w, y: -hz + w },
        { x: -hx + w, y: hz - w },
        { x: hx, y: hz - w },
        { x: hx, y: hz },
        { x: -hx, y: hz },
      ];
    case 2:
      // L: two legs off one corner.
      return [
        { x: -hx, y: -hz },
        { x: hx, y: -hz },
        { x: hx, y: -hz + w },
        { x: -hx + w, y: -hz + w },
        { x: -hx + w, y: hz },
        { x: -hx, y: hz },
      ];
    default:
      return [
        { x: -hx, y: -hz },
        { x: hx, y: -hz },
        { x: hx, y: hz },
        { x: -hx, y: hz },
      ];
  }
}
